/**
 * Spodný dok (docs/design/5-hra.png): vľavo akcie kola, v strede ruka
 * s balíčkom a odhodom, vpravo koniec ťahu — všetko o ťahu na jednom mieste,
 * tam, kam sa hráč pozerá, keď hrá.
 *
 * Tri akcie sú vždy vidieť (aj keď sa nedajú použiť) — pravidlo „dve akcie
 * za ťah, pokojne aj dve rovnaké" je tak čitateľné z obrazovky, nie len
 * z pravidiel. Nápoveda k ťahu je nad doskou (`.boardHint`), nie tu.
 */

import type { ActionKind } from '@unmatched/engine';
import type React from 'react';

export type ActionMode = 'maneuver' | 'scheme' | 'attack';

const ACTION_META: Record<ActionMode, { label: string; icon: string }> = {
  maneuver: { label: 'Manéver', icon: '/art/icons/action-maneuver.svg' },
  scheme: { label: 'Schéma', icon: '/art/icons/action-scheme.svg' },
  attack: { label: 'Útok', icon: '/art/icons/action-attack.svg' },
};

interface Props {
  actionsUsed: ActionKind[];
  /** Koľko akcií má hráč v tomto ťahu (2 + získané navyše). */
  actionsAllowed?: number;
  /** Možnosti efektu („choose 1 effect") — tlačidlá nad koncom ťahu. */
  choices?: { label: string; onClick: () => void }[];
  /** Ktoré akcie sa dajú práve začať. */
  available: Record<ActionMode, boolean>;
  /** Zvýraznená akcia — buď prebiehajúci manéver, alebo zvolený režim výberu karty. */
  activeMode: ActionMode | null;
  busy: boolean;
  /** Prebieha rozhodnutie z efektu karty — akcie ťahu sú dovtedy mimo hry. */
  pending?: boolean;
  canEndTurn: boolean;
  /** Zobrazí sa namiesto konca ťahu, keď treba dokončiť manéver alebo potvrdiť efekt. */
  finish?: { label: string; onClick: () => void } | null;
  secondary?: { label: string; onClick: () => void } | null;
  onPick: (mode: ActionMode) => void;
  onEndTurn: () => void;
  /** Ruka s balíčkom a odhodom — stred doku. */
  children: React.ReactNode;
}

export function ActionBar({
  actionsUsed,
  actionsAllowed = 2,
  choices = [],
  available,
  activeMode,
  busy,
  pending,
  canEndTurn,
  finish,
  secondary,
  onPick,
  onEndTurn,
  children,
}: Props) {
  return (
    <footer className="dock">
      <div className="dock__actions">
        <div className="roundPips" title="Za ťah máš dve akcie">
          <span className="roundPips__label">
            Akcie kola
            <b>
              {actionsUsed.length} / {actionsAllowed}
            </b>
          </span>
          <span className="roundPips__dots">
            {Array.from({ length: Math.max(actionsAllowed, actionsUsed.length) }, (_, slot) => slot).map((slot) => (
              <span key={slot} className={`pip ${actionsUsed[slot] ? 'is-used' : ''}`} />
            ))}
          </span>
        </div>
        <div className="dock__buttons">
          {(Object.keys(ACTION_META) as ActionMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              className={`action action--${mode} ${activeMode === mode ? 'is-active' : ''}`}
              disabled={busy || pending || (!available[mode] && activeMode !== mode)}
              onClick={() => onPick(mode)}
            >
              <img src={ACTION_META[mode].icon} alt="" />
              {ACTION_META[mode].label}
            </button>
          ))}
        </div>
      </div>

      {children}

      <div className="dock__end">
        {choices.map((choice) => (
          <button
            key={choice.label}
            type="button"
            className="btn btn--primary"
            disabled={busy}
            onClick={choice.onClick}
          >
            {choice.label}
          </button>
        ))}
        {secondary ? (
          <button type="button" className="btn" disabled={busy} onClick={secondary.onClick}>
            {secondary.label}
          </button>
        ) : null}
        {finish ? (
          <button
            type="button"
            className="btn btn--primary btn--lg"
            disabled={busy}
            onClick={finish.onClick}
          >
            {finish.label}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--inverted btn--lg endTurn"
            disabled={!canEndTurn || busy}
            onClick={onEndTurn}
          >
            Ukončiť ťah →
          </button>
        )}
      </div>
    </footer>
  );
}
